export const getSupplierSource = (supplierTab) => {
  if (supplierTab === "all") return "asi-live";
  if (supplierTab === "connected") return "preferred";
  return "custom";
};

export const isLiveAsiSupplierSource = (supplierSource) => supplierSource === "asi-live";

export const isSupplierAdministrator = (role) =>
  ["administrator", "admin", "superadmin", "super-administrator"].includes(
    String(role || "").trim().replace(/_/g, "-").toLowerCase()
  );

export const canManagePreferredSuppliers = (role) => isSupplierAdministrator(role);

const contactValue = (contact, ...keys) => {
  const key = keys.find((candidate) => contact?.[candidate] != null);
  return key ? String(contact[key]).trim() : "";
};

const textValue = (value) => value == null ? "" : String(value).trim();
const objectValue = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const fieldValue = (object, ...keys) => {
  const source = objectValue(object);
  const key = keys.find((candidate) => source[candidate] != null);
  return key ? textValue(source[key]) : "";
};

const COUNTRY_LABELS = {
  CAN: "Canada",
  CA: "Canada",
  USA: "United States",
  US: "United States",
};

export const formatAsiAddress = (address) => {
  const source = objectValue(address);
  const country = fieldValue(source, "Country", "country");
  return [
    fieldValue(source, "Street1", "street1"),
    fieldValue(source, "Street2", "street2"),
    fieldValue(source, "City", "city"),
    [fieldValue(source, "State", "state"), fieldValue(source, "Zip", "zip", "PostalCode", "postalCode")]
      .filter(Boolean)
      .join(" "),
    COUNTRY_LABELS[country.toUpperCase()] || country,
  ].filter(Boolean).join(", ");
};

const normalizeStringList = (value) => {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((item) => textValue(
    typeof item === "object" ? item?.Name ?? item?.name : item
  )).filter(Boolean))];
};

const safeExternalUrl = (value) => {
  const url = textValue(value);
  return /^https?:\/\/[^\s]+$/i.test(url) ? url : "";
};

const normalizeAddresses = (addresses) => {
  const source = objectValue(addresses);
  const seen = new Set();
  return [
    ["Primary Address", source.Primary ?? source.primary],
    ["Secondary Address", source.Secondary ?? source.secondary],
    ["Delivery Address", source.Delivery ?? source.delivery],
  ].reduce((result, [label, address]) => {
    const formatted = formatAsiAddress(address);
    const duplicateKey = formatted.toLocaleLowerCase();
    if (!formatted || seen.has(duplicateKey)) return result;
    seen.add(duplicateKey);
    result.push({ label, formatted });
    return result;
  }, []);
};

export const normalizeAsiOverview = (payload) => {
  const source = objectValue(payload);
  const orders = objectValue(source.Orders ?? source.orders);
  const artwork = objectValue(source.Artwork ?? source.artwork);
  const productionTime = objectValue(source.ProductionTime ?? source.productionTime);
  const links = objectValue(source.Links ?? source.links);
  const phone = objectValue(source.Phone ?? source.phone);
  const documentations = Array.isArray(source.Documentations ?? source.documentations)
    ? source.Documentations ?? source.documentations
    : [];
  const knownSocialKeys = new Set(["facebook", "twitter", "x", "linkedin", "youtube", "instagram"]);
  const recognizedSocialLinks = [
    ["Facebook", links.Facebook ?? links.facebook],
    ["Twitter", links.Twitter ?? links.twitter],
    ["X", links.X ?? links.x],
    ["LinkedIn", links.Linkedin ?? links.LinkedIn ?? links.linkedin],
    ["YouTube", links.Youtube ?? links.YouTube ?? links.youtube],
    ["Instagram", links.Instagram ?? links.instagram],
  ];
  const additionalSocialLinks = Object.entries(links)
    .filter(([key]) => !knownSocialKeys.has(key.toLowerCase()))
    .map(([label, url]) => [label, url]);

  return {
    generalEmail: fieldValue(source, "Email", "email"),
    phone: fieldValue(phone, "Work", "work") || fieldValue(source, "PhoneNumber", "phoneNumber"),
    tollFree: fieldValue(phone, "TollFree", "tollFree", "tollfree"),
    fax: fieldValue(source, "Fax", "fax"),
    ordersEmail: fieldValue(orders, "Email", "email"),
    artworkEmail: fieldValue(artwork, "Email", "email"),
    officeHours: fieldValue(source, "OfficeHours", "officeHours"),
    productionTime: fieldValue(productionTime, "Name", "name") || (
      fieldValue(productionTime, "Days", "days")
        ? `${fieldValue(productionTime, "Days", "days")} Days`
        : ""
    ),
    type: fieldValue(source, "Type", "type"),
    yearEstablished: fieldValue(source, "YearEstablished", "yearEstablished"),
    yearInIndustry: fieldValue(source, "YearInIndustry", "yearInIndustry"),
    totalEmployees: fieldValue(source, "TotalEmployees", "totalEmployees"),
    distributionPolicy: fieldValue(source, "DistributionPolicy", "distributionPolicy"),
    marketingPolicy: fieldValue(source, "MarketingPolicy", "marketingPolicy"),
    story: fieldValue(source, "OurStory", "ourStory"),
    functions: normalizeStringList(source.Functions ?? source.functions),
    imprintingMethods: normalizeStringList(source.ImprintingMethods ?? source.imprintingMethods),
    addresses: normalizeAddresses(source.Addresses ?? source.addresses),
    resources: documentations.map((resource) => ({
      name: fieldValue(resource, "Name", "name"),
      url: safeExternalUrl(resource?.Url ?? resource?.url),
    })).filter((resource) => resource.name && resource.url),
    socialLinks: [...recognizedSocialLinks, ...additionalSocialLinks]
      .map(([label, url]) => ({ label, url: safeExternalUrl(url) }))
      .filter((link) => link.url),
  };
};

export const normalizeAsiContacts = (contacts) => {
  if (!Array.isArray(contacts)) return [];

  return contacts
    .filter((contact) => contact && typeof contact === "object" && !Array.isArray(contact))
    .map((contact) => ({
      name: contactValue(contact, "Name", "name"),
      title: contactValue(contact, "Title", "title"),
      email: contactValue(contact, "Email", "email"),
      phone: contactValue(contact, "Phone", "phone"),
    }))
    .filter((contact) => Object.values(contact).some(Boolean));
};
